import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabase";
import {
  type CategoryDbRow,
  type CategoryItem,
} from "@/features/admin/categories/store/admin-categories-store";

export function resolveCategoryImage(_name?: string, rawImage?: string | null): string {
  return rawImage?.trim() || "";
}

export const SEED_CATEGORIES: CategoryItem[] = [
  {
    id: "cat-1",
    name: "Artificial Bonsai",
    slug: "artificial-bonsai",
    parentId: null,
    description: "Traditional Japanese asymmetrical art bonsai & potted pines",
    image: "/plantpicture/p1/5.webp",
    displayOrder: 1,
    status: "ACTIVE",
    productsCount: 19,
    seoTitle: "Artificial Bonsai | Aster Decorz",
    seoDescription: "Traditional Japanese asymmetrical art bonsai & potted pines",
    seoKeywords: "artificial bonsai, bonsai trees, japanese bonsai",
    createdAt: "2026-08-29T08:03:40.458+00:00",
  },
  {
    id: "cat-2",
    name: "Trees & Plants",
    slug: "trees-plants",
    parentId: null,
    description: "5 FT tall statement trees, flowering canopies, palms & bamboo",
    image: "/plantpicture/1.jpg.webp",
    displayOrder: 2,
    status: "ACTIVE",
    productsCount: 18,
    seoTitle: "Trees & Plants | Aster Decorz",
    seoDescription: "5 FT tall statement trees, flowering canopies, palms & bamboo",
    seoKeywords: "trees, tall plants, artificial trees, palms, ficus",
    createdAt: "2026-08-29T08:03:40.458+00:00",
  },
  {
    id: "cat-3",
    name: "Flower Bushes",
    slug: "flower-bushes",
    parentId: null,
    description: "Vibrant blooming bougainvillea, trumpet lilies, sunflowers & rose shrubs",
    image: "/plantpicture/p3/15.webp",
    displayOrder: 3,
    status: "ACTIVE",
    productsCount: 9,
    seoTitle: "Flower Bushes | Aster Decorz",
    seoDescription: "Vibrant blooming bougainvillea, trumpet lilies, sunflowers & rose shrubs",
    seoKeywords: "flower bushes, blooming bushes, rose shrubs",
    createdAt: "2026-08-29T08:03:40.458+00:00",
  },
  {
    id: "cat-4",
    name: "Hanging",
    slug: "hanging",
    parentId: null,
    description: "Cascading floral vines, trailing creepers & wall-hanging greenery",
    image: "/plantpicture/2.jpg.webp",
    displayOrder: 4,
    status: "ACTIVE",
    productsCount: 8,
    seoTitle: "Hanging Plants & Vines | Aster Decorz",
    seoDescription: "Cascading floral vines, trailing creepers & wall-hanging greenery",
    seoKeywords: "hanging plants, vines, creepers, cascading flowers",
    createdAt: "2026-08-29T08:03:40.458+00:00",
  },
  {
    id: "cat-5",
    name: "Flower Plants and Pots",
    slug: "flower-plants-and-pots",
    parentId: null,
    description: "Potted tropical foliage, lotus blossoms & designer planters",
    image: "/plantpicture/4.jpg.webp",
    displayOrder: 5,
    status: "ACTIVE",
    productsCount: 11,
    seoTitle: "Flower Plants and Pots | Aster Decorz",
    seoDescription: "Potted tropical foliage, lotus blossoms & designer planters",
    seoKeywords: "potted plants, flower plants, ceramic pots, monstera, calathea",
    createdAt: "2026-08-29T08:03:40.458+00:00",
  },
];

export function useLiveCategories() {
  const [categories, setCategories] = useState<CategoryItem[]>(SEED_CATEGORIES);
  const [loading, setLoading] = useState(false);

  const fetchCategories = useCallback(async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("categories")
        .select("*")
        .order("display_order", { ascending: true });

      if (!error && data && data.length > 0) {
        const mapped: CategoryItem[] = data.map((row: CategoryDbRow) => ({
          id: row.id || `cat-${row.slug}`,
          name: row.name,
          slug: row.slug || row.name.toLowerCase().replace(/[^a-z0-9]+/g, "-"),
          parentId: row.parent_id || null,
          description: row.description || "",
          image: resolveCategoryImage(row.name, row.image),
          bannerImage: row.banner_image || undefined,
          displayOrder: Number(row.display_order) || 1,
          status: (row.status?.toUpperCase() as CategoryItem["status"]) || "ACTIVE",
          productsCount: Number(row.products_count) || 0,
          seoTitle: row.seo_title || `${row.name} | Aster Decorz`,
          seoDescription: row.seo_description || row.description || "",
          seoKeywords: row.seo_keywords || "plants, category",
          createdAt: row.created_at || new Date().toISOString(),
        }));
        setCategories(mapped);
      } else {
        setCategories((prev) => (prev.length > 0 ? prev : SEED_CATEGORIES));
      }
    } catch {
      setCategories((prev) => (prev.length > 0 ? prev : SEED_CATEGORIES));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCategories();

    const channelName = `categories-sync-${Math.random().toString(36).substring(2, 9)}`;
    const channel = supabase
      .channel(channelName)
      .on("postgres_changes", { event: "*", schema: "public", table: "categories" }, () => {
        fetchCategories();
      })
      .subscribe();

    const handleLocalUpdate = () => {
      fetchCategories();
    };

    window.addEventListener("categories_updated", handleLocalUpdate);
    window.addEventListener("storage", handleLocalUpdate);

    let bc: BroadcastChannel | null = null;
    try {
      bc = new BroadcastChannel("categories_sync");
      bc.onmessage = () => {
        fetchCategories();
      };
    } catch {
      // Ignored
    }

    return () => {
      supabase.removeChannel(channel);
      window.removeEventListener("categories_updated", handleLocalUpdate);
      window.removeEventListener("storage", handleLocalUpdate);
      bc?.close();
    };
  }, [fetchCategories]);

  return { categories, loading, refetch: fetchCategories };
}
